import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Heart, Mic, MicOff, PhoneOff, Sparkles, Video, VideoOff, X, CloudSun, Music } from "lucide-react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ExhibitionBooth } from "@/hooks/useExhibition";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import { useBoothBalloons } from "@/hooks/useBoothBalloons";
import { useBoothModeration } from "@/hooks/useBoothModeration";
import { useBoothWebRTC } from "@/hooks/useBoothWebRTC";
import { useBoothBans } from "@/hooks/useBoothBans";
import { useBoothJoinRequests } from "@/hooks/useBoothJoinRequests";
import BoothMatchModal from "./BoothMatchModal";
import BoothModerationBanner from "./BoothModerationBanner";
import KickedFromBoothModal from "./KickedFromBoothModal";
import BoothAdminDropdown from "./BoothAdminDropdown";
import BoothPreJoinModal from "./BoothPreJoinModal";
import LoveMemoryGallery from "./LoveMemoryGallery";
import EchoLoveNoteModal from "./EchoLoveNoteModal";
import VideoBoothGrid from "./VideoBoothGrid";
import { toast } from "sonner";
import { useAuthContext } from "@/contexts/AuthContext";

type Mode = "ambient" | "veil" | "video";

type PresencePayload = {
  id: string;
  intent?: string;
  tzOffsetMin?: number;
  activityTag?: "morning_coffee" | "evening_walk" | "weekend_downtime" | "late_night";
  aura?: "flirty" | "romantic" | "serious";
  online_at: string;
};

type BoothSilhouette = {
  id: string;
  aura: "flirty" | "romantic" | "serious";
  activityTag?: PresencePayload["activityTag"];
  tzOffsetMin?: number;
  intent?: string;
};

const LOVE_AURA = {
  flirty: { label: "Flirty", ring: "ring-pink-400/60", glow: "bg-pink-400/15", dot: "bg-pink-400", gradient: "from-pink-500 to-rose-500" },
  romantic: { label: "Romantic", ring: "ring-red-500/60", glow: "bg-red-500/15", dot: "bg-red-500", gradient: "from-red-500 to-rose-600" },
  serious: { label: "Serious", ring: "ring-amber-500/60", glow: "bg-amber-500/15", dot: "bg-amber-500", gradient: "from-amber-500 to-yellow-500" },
} as const;

function pickAura(intent: string | undefined): BoothSilhouette["aura"] {
  if (!intent) return "flirty";
  const lower = intent.toLowerCase();
  if (lower === "serious" || lower === "elite") return "serious";
  if (lower === "flirting" || lower === "fun" || lower === "casual") return "flirty";
  return "romantic";
}

function localHourForOffset(offsetMin: number | undefined) {
  const base = new Date();
  const utcMs = base.getTime() + base.getTimezoneOffset() * 60_000;
  const localMs = utcMs + (offsetMin ?? 0) * 60_000;
  return new Date(localMs).getHours();
}

function activityTagFromHour(h: number): PresencePayload["activityTag"] {
  if (h >= 6 && h <= 11) return "morning_coffee";
  if (h >= 17 && h <= 20) return "evening_walk";
  if (h >= 21 || h <= 1) return "late_night";
  return "weekend_downtime";
}

function hasOverlappingIntent(userIntent: string | null, otherIntent: string | undefined): boolean {
  if (!userIntent || !otherIntent) return true; // Show all if no intent specified
  return userIntent.toLowerCase() === otherIntent.toLowerCase();
}

interface BoothArenaViewProps {
  booth: ExhibitionBooth;
  participants: ArenaParticipant[];
  currentUserId?: string;
  cityName?: string;
  countryName?: string;
  eventEndsAt?: string;
  eventId?: string; // Optional event ID for linking love notes to events
  onLeave: () => void;
  onEndBooth: () => void;
}

const BoothArenaView: React.FC<BoothArenaViewProps> = ({
  booth,
  participants,
  currentUserId,
  cityName,
  countryName,
  eventEndsAt,
  eventId,
  onLeave,
  onEndBooth,
}) => {
  const { user, profile } = useAuthContext();
  const intent = booth.exhibition_intent || "casual";
  const isAdmin = booth.created_by === currentUserId;
  const currentParticipant = participants.find(p => p.user_id === currentUserId);
  const currentUserName = currentParticipant?.profile?.name;

  const channelRef = useRef<RealtimeChannel | null>(null);
  const mountedRef = useRef(false);

  const [showPreJoin, setShowPreJoin] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const [mode, setMode] = useState<Mode>("ambient");
  const [veilSecondsLeft, setVeilSecondsLeft] = useState<number>(0);
  const [myStream, setMyStream] = useState<MediaStream | null>(null);
  const myStreamRef = useRef<MediaStream | null>(null);
  myStreamRef.current = myStream;

  // New features state
  const [showMemoryGallery, setShowMemoryGallery] = useState(false);
  const [showEchoLoveNote, setShowEchoLoveNote] = useState(false);
  const [showSpeakQueue, setShowSpeakQueue] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [currentSpeakerId, setCurrentSpeakerId] = useState<string | null>(null);
  const [lastInteraction, setLastInteraction] = useState<{ type: "pop" | "match" | "resonance"; userId?: string } | null>(null);
  const [showMatchModal, setShowMatchModal] = useState<{ userId: string; userName: string } | null>(null);
  const [kickedReason, setKickedReason] = useState<string | null>(null);
  const [sparkCardUserId, setSparkCardUserId] = useState<string | null>(null);

  const [presence, setPresence] = useState<Record<string, PresencePayload[]>>({});

  // WebRTC state management
  const {
    localStream,
    isVideoEnabled,
    isAudioEnabled,
    permissionGranted,
    permissionDenied,
    isInitializing,
    error: mediaError,
    requestPermissions,
    toggleVideo,
    toggleAudio,
    forceMute,
    forceVideoOff,
    stopStream,
  } = useBoothWebRTC(booth.id);

  // Balloon management with pop limits
  const {
    getBalloon,
    currentUserBalloon,
    popBalloon,
    getPopCount,
    popsRemaining,
  } = useBoothBalloons(booth.id, currentUserId, currentUserName);

  // Moderation callbacks
  const handleMutedByAdmin = useCallback(() => {
    forceMute();
    toast.warning("Your audio has been muted by the booth admin");
  }, [forceMute]);

  const handleUnmutedByAdmin = useCallback(() => {
    toast.success("Your audio has been unmuted");
  }, []);

  const handleCameraDisabledByAdmin = useCallback(() => {
    forceVideoOff();
    toast.warning("Your camera has been disabled by the booth admin");
  }, [forceVideoOff]);

  const handleCameraEnabledByAdmin = useCallback(() => {
    toast.success("Your camera has been enabled");
  }, []);

  const handleKickedFromBooth = useCallback((reason?: string) => {
    setKickedReason(reason || "You have been removed from this booth");
    stopStream();
  }, [stopStream]);

  const handleBannedFromBooth = useCallback((reason?: string) => {
    setKickedReason(reason || "You have been banned from this booth");
    stopStream();
  }, [stopStream]);

  // Booth moderation
  const {
    moderationState,
    canModerate,
    muteParticipant,
    unmuteParticipant,
    disableCamera,
    enableCamera,
    kickParticipant,
    banParticipant,
    isUserMuted,
    isUserCameraDisabled,
  } = useBoothModeration(
    booth.id,
    currentUserId,
    booth.created_by,
    handleMutedByAdmin,
    handleUnmutedByAdmin,
    handleCameraDisabledByAdmin,
    handleCameraEnabledByAdmin,
    handleKickedFromBooth,
    handleBannedFromBooth
  );

  // Ban persistence
  const { banUser, isCurrentUserBanned } = useBoothBans(booth.id, currentUserId);

  // Pending join requests (for admin)
  const { pendingRequests, admitUser, rejectRequest } = useBoothJoinRequests(booth.id, isAdmin);

  // Handle ban with persistence
  const handleBanParticipant = useCallback(async (userId: string, reason?: string) => {
    const success = await banUser(userId, currentUserId!, reason);
    if (success) {
      banParticipant(userId, reason);
    }
  }, [banUser, banParticipant, currentUserId]);

  // Ambient audio based on booth intent/theme
  const audioSrc = useMemo(() => {
    // Romantic soundscapes - can be replaced with actual hosted assets
    switch (intent) {
      case "serious":
      case "elite":
        return "https://cdn.pixabay.com/download/audio/2021/11/09/audio_6c3cdd43aa.mp3?filename=cafe-ambience-ambient-7251.mp3"; // Elegant café
      case "flirting":
      case "fun":
        return "https://cdn.pixabay.com/download/audio/2022/02/23/audio_333f0cb1cf.mp3?filename=city-ambience-101189.mp3"; // City night
      case "faith":
        return "https://cdn.pixabay.com/download/audio/2022/03/15/audio_b9f07a6a0b.mp3?filename=sea-waves-ambient-110397.mp3"; // Peaceful waves
      default:
        return "https://cdn.pixabay.com/download/audio/2021/08/04/audio_5a3d11d3c0.mp3?filename=wind-ambient-6195.mp3"; // Soft wind
    }
  }, [intent]);

  // Join realtime presence for silhouettes
  useEffect(() => {
    if (mountedRef.current) return;
    if (!user || !profile) return;
    mountedRef.current = true;

    const channelName = `booth-arena:${booth.id}`;
    const channel = supabase.channel(channelName);

    channel.on("presence", { event: "sync" }, () => {
      setPresence(channel.presenceState() as any);
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        const tzOffsetMin = -new Date().getTimezoneOffset();
        const activityTag = activityTagFromHour(localHourForOffset(tzOffsetMin));
        const aura = pickAura(intent);

        await channel.track({
          id: user.id,
          intent,
          tzOffsetMin,
          activityTag,
          aura,
          online_at: new Date().toISOString(),
        });
      }
    });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      mountedRef.current = false;
    };
  }, [user, profile, booth.id, intent]);

  // Extract silhouettes from presence
  const silhouettes = useMemo(() => {
    const all: BoothSilhouette[] = [];
    Object.values(presence).forEach((presences) => {
      presences.forEach((p) => {
        if (p.id !== user?.id) {
          all.push({
            id: p.id,
            aura: pickAura(p.intent),
            activityTag: p.activityTag,
            tzOffsetMin: p.tzOffsetMin,
            intent: p.intent,
          });
        }
      });
    });
    // Filter by overlapping intent
    return all.filter((s) => hasOverlappingIntent(intent, s.intent)).slice(0, 12);
  }, [presence, user?.id, intent]);

  // Love Resonance: detect shared local rhythms
  const hasResonance = useCallback((silhouette: BoothSilhouette) => {
    if (!silhouette.activityTag || !silhouette.tzOffsetMin) return false;
    const myTz = -new Date().getTimezoneOffset();
    const myHour = localHourForOffset(myTz);
    const theirHour = localHourForOffset(silhouette.tzOffsetMin);
    const myTag = activityTagFromHour(myHour);
    return myTag === silhouette.activityTag && Math.abs(myTz - silhouette.tzOffsetMin) < 120; // Within 2 hours
  }, []);

  // Connection Weather (playful forecast)
  const connectionWeather = useMemo(() => {
    const hour = new Date().getHours();
    const participantCount = participants.length;
    const isEvening = hour >= 17 && hour <= 22;
    const isWeekend = [0, 6].includes(new Date().getDay());

    if (isEvening && isWeekend) {
      return {
        temp: "82°F",
        condition: "High Romance Humidity",
        chance: "95% Chance of Sparks",
        energy: "Breezy Flirt Energy",
      };
    } else if (isEvening) {
      return {
        temp: "78°F",
        condition: "Warm Connection Vibes",
        chance: "85% Chance of Sparks",
        energy: "Gentle Evening Flow",
      };
    } else {
      return {
        temp: "72°F",
        condition: "Fresh Morning Energy",
        chance: "70% Chance of Sparks",
        energy: "Bright New Connections",
      };
    }
  }, [participants.length]);

  // Golden Hour indicator (peak synchronized activity)
  const isGoldenHour = useMemo(() => {
    const hour = new Date().getHours();
    return (hour >= 19 && hour <= 21) && [5, 6].includes(new Date().getDay()); // Friday/Saturday evening
  }, []);

  // Handle joining the booth after pre-join
  const handleJoinBooth = useCallback(() => {
    if (permissionGranted) {
      setShowPreJoin(false);
      setHasJoined(true);
      setMode("ambient"); // Start in Ambient Mode
      toast.success(`Welcome to ${booth.name}! 💖`, {
        description: "You're in Ambient Mode - step forward when ready",
      });
    }
  }, [permissionGranted, booth.name]);

  // Handle "Step Forward into Exhibition"
  const handleStepForward = useCallback(async () => {
    if (!permissionGranted) {
      await requestPermissions();
      return;
    }
    setMode("veil");
    setVeilSecondsLeft(45); // 45-second observer veil
  }, [permissionGranted, requestPermissions]);

  // Observer Veil countdown
  useEffect(() => {
    if (mode !== "veil" || veilSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setVeilSecondsLeft((prev) => {
        if (prev <= 1) {
          setMode("video");
          setMyStream(localStream);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [mode, veilSecondsLeft, localStream]);

  // Host detection (first video user)
  useEffect(() => {
    if (mode === "video" && participants.length > 0) {
      const firstVideoUser = participants.find((p) => p.is_video_on && p.user_id !== currentUserId);
      if (!firstVideoUser && isVideoEnabled) {
        setIsHost(true);
        setCurrentSpeakerId(currentUserId || null);
      } else if (firstVideoUser && !isHost) {
        setIsHost(false);
        setCurrentSpeakerId(firstVideoUser.user_id);
      }
    }
  }, [mode, participants, isVideoEnabled, currentUserId, isHost]);

  // Spark Chain Reactions
  const triggerSparkChainReaction = useCallback(async (userId: string, type: "pop" | "match" | "resonance") => {
    if (!user) return;

    // Find 3-5 nearby compatible auras
    const compatible = silhouettes
      .filter((s) => s.id !== userId && hasResonance(s))
      .slice(0, 5);

    if (compatible.length === 0) return;

    // Send gentle wave notifications (via realtime)
    const channel = channelRef.current;
    if (channel) {
      await channel.send({
        type: "broadcast",
        event: "spark_chain",
        payload: {
          triggered_by: user.id,
          type,
          target_user_ids: compatible.map((s) => s.id),
          message: "A spark just happened nearby! Join a 30-second group toast?",
        },
      });
    }
  }, [user, silhouettes, hasResonance]);

  // Handle balloon pop with match detection
  const handlePopBalloon = useCallback(async (targetUserId: string) => {
    const isMatch = await popBalloon(targetUserId);
    if (isMatch) {
      const targetParticipant = participants.find(p => p.user_id === targetUserId);
      setShowMatchModal({
        userId: targetUserId,
        userName: targetParticipant?.profile?.name || "Someone",
      });
      setLastInteraction({ type: "match", userId: targetUserId });
      triggerSparkChainReaction(targetUserId, "match");
      
      // Auto-offer Echo Love Note after 2 seconds
      setTimeout(() => {
        setShowEchoLoveNote(true);
      }, 2000);
    } else {
      setLastInteraction({ type: "pop", userId: targetUserId });
      triggerSparkChainReaction(targetUserId, "pop");
    }
  }, [popBalloon, participants, triggerSparkChainReaction]);

  // Handle leaving
  const handleLeave = useCallback(() => {
    stopStream();
    onLeave();
  }, [stopStream, onLeave]);

  // Romantic Spark AI icebreaker generator
  const generateSparkCard = useCallback((silhouette: BoothSilhouette) => {
    const resonating = hasResonance(silhouette);
    const auraLabel = LOVE_AURA[silhouette.aura].label;
    
    const icebreakers = [
      resonating
        ? `Both in ${silhouette.activityTag?.replace("_", " ")} mode? In this ${booth.name} booth, try whispering a favorite love song lyric.`
        : `Your ${auraLabel} aura is glowing! In this ${booth.name} booth, share a moment that made your heart skip.`,
      `Love is in the air at ${booth.name}. What's one thing that makes you smile?`,
      `Your energy matches perfectly here. What's your favorite way to connect?`,
    ];
    
    return icebreakers[Math.floor(Math.random() * icebreakers.length)];
  }, [booth.name, hasResonance]);

  // If pre-join is showing
  if (showPreJoin) {
    return (
      <BoothPreJoinModal
        isOpen={true}
        boothName={booth.name}
        stream={localStream}
        isInitializing={isInitializing}
        permissionDenied={permissionDenied}
        error={mediaError}
        isVideoEnabled={isVideoEnabled}
        isAudioEnabled={isAudioEnabled}
        onToggleVideo={toggleVideo}
        onToggleAudio={toggleAudio}
        onRequestPermissions={requestPermissions}
        onJoin={handleJoinBooth}
        onCancel={onLeave}
      />
    );
  }

  // If current user is banned
  if (isCurrentUserBanned) {
    return (
      <KickedFromBoothModal
        isOpen={true}
        boothName={booth.name}
        reason="You have been banned from this booth and cannot rejoin."
        onClose={onLeave}
        onGoHome={onLeave}
      />
    );
  }

  // AMBIENT MODE
  if (mode === "ambient") {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 flex flex-col">
        {/* Connection Weather Banner */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/90 backdrop-blur-sm rounded-full px-6 py-2 shadow-lg border border-rose-200/50">
          <div className="flex items-center gap-3 text-sm">
            <CloudSun className="w-5 h-5 text-amber-500" />
            <div className="text-center">
              <div className="font-semibold text-foreground">
                {connectionWeather.temp} {connectionWeather.condition}
              </div>
              <div className="text-xs text-muted-foreground">
                {connectionWeather.chance} • {connectionWeather.energy}
              </div>
            </div>
          </div>
        </div>

        {/* Golden Hour Indicator */}
        {isGoldenHour && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-r from-amber-400 to-yellow-400 text-white rounded-full px-4 py-1.5 text-xs font-semibold shadow-lg animate-pulse">
            ✨ Golden Hour: Peak Synchronized Activity ✨
          </div>
        )}

        {/* Ambient Audio (hidden) */}
        <audio src={audioSrc} loop autoPlay className="hidden" />

        {/* Silhouettes */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="grid grid-cols-3 md:grid-cols-4 gap-6 max-w-4xl">
            {silhouettes.map((silhouette, idx) => {
              const aura = LOVE_AURA[silhouette.aura];
              const resonating = hasResonance(silhouette);
              
              return (
                <button
                  key={silhouette.id}
                  onClick={() => setSparkCardUserId(silhouette.id)}
                  className={cn(
                    "relative w-24 h-24 rounded-full flex items-center justify-center",
                    "bg-gradient-to-br", aura.gradient,
                    "shadow-lg border-2", aura.ring,
                    resonating && "animate-pulse",
                    "transition-all hover:scale-110 hover:shadow-xl"
                  )}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <Heart className="w-12 h-12 text-white/80" />
                  <div className={cn("absolute -top-1 -right-1 w-3 h-3 rounded-full", aura.dot)} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Romantic Spark AI Card */}
        {sparkCardUserId && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md shadow-2xl">
              <div className="flex items-start justify-between mb-4">
                <Sparkles className="w-6 h-6 text-rose-500" />
                <button onClick={() => setSparkCardUserId(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-foreground mb-4">
                {generateSparkCard(silhouettes.find(s => s.id === sparkCardUserId)!)}
              </p>
              <Button onClick={() => setSparkCardUserId(null)} className="w-full">
                Got it! 💖
              </Button>
            </div>
          </div>
        )}

        {/* Step Forward Button */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-4">
          <Button
            onClick={handleStepForward}
            size="lg"
            className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-2xl hover:shadow-rose-500/50 transition-all hover:scale-105"
          >
            Step Forward into the Exhibition
          </Button>
          <button
            onClick={() => setMode("ambient")}
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            Stay in Ambient
          </button>
        </div>
      </div>
    );
  }

  // OBSERVER VEIL MODE
  if (mode === "veil") {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 flex flex-col items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 mx-auto flex items-center justify-center shadow-2xl">
            <Heart className="w-16 h-16 text-white/80" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Observer Veil</h2>
            <p className="text-muted-foreground mb-4">
              You're appearing as an anonymous silhouette to others
            </p>
            <div className="text-4xl font-bold text-rose-500">
              {veilSecondsLeft}s
            </div>
          </div>
        </div>
      </div>
    );
  }

  // VIDEO MODE - Full booth experience
  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b space-y-2 bg-gradient-to-r from-rose-50 to-pink-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={cn(
              "px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase",
              "bg-gradient-to-r",
              pickAura(intent) === "flirty" && "from-pink-500 to-rose-500",
              pickAura(intent) === "romantic" && "from-red-500 to-rose-600",
              pickAura(intent) === "serious" && "from-amber-500 to-yellow-500"
            )}>
              {intent}
            </div>
            <h2 className="font-bold text-foreground truncate">{booth.name}</h2>
          </div>
          {canModerate && (
            <BoothAdminDropdown
              participants={participants}
              pendingRequests={pendingRequests}
              currentUserId={currentUserId}
              localStream={localStream}
              isUserMuted={isUserMuted}
              isUserCameraDisabled={isUserCameraDisabled}
              onMute={muteParticipant}
              onUnmute={unmuteParticipant}
              onDisableCamera={disableCamera}
              onEnableCamera={enableCamera}
              onKick={kickParticipant}
              onBan={handleBanParticipant}
              onAdmitUser={admitUser}
              onRejectRequest={rejectRequest}
              onEndBooth={onEndBooth}
            />
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span>{cityName || "Live Booth"}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 font-semibold text-foreground">
              {participants.length}/{booth.max_participants}
            </div>
          </div>
        </div>

        {currentUserBalloon && (
          <div className="text-center">
            <span className="text-xs text-muted-foreground">
              🎈 You're Balloon #{currentUserBalloon.balloonNumber} • {popsRemaining} pops remaining
            </span>
          </div>
        )}
      </div>

      {/* Moderation Banner */}
      {(moderationState.mutedByAdmin || moderationState.cameraDisabledByAdmin) && (
        <div className="px-4 py-2 border-b">
          <BoothModerationBanner
            isMutedByAdmin={moderationState.mutedByAdmin}
            isCameraDisabledByAdmin={moderationState.cameraDisabledByAdmin}
          />
        </div>
      )}

      {/* Video Grid - Enhanced with customizable balloons */}
      <div className="flex-1 overflow-y-auto p-4 min-h-[300px] bg-gradient-to-b from-background to-muted/20">
        <VideoBoothGrid
          participants={participants}
          currentUserId={currentUserId}
          adminId={booth.created_by}
          localStream={localStream}
          getBalloon={getBalloon}
          getPopCount={getPopCount}
          popsRemaining={popsRemaining}
          onPopBalloon={handlePopBalloon}
        />
      </div>

      {/* Bottom Navigation */}
      <div className="px-4 py-3 border-t bg-background flex items-center justify-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            // Pop balloon action is handled in VideoBoothGrid via onPopBalloon
            toast.info("Tap a balloon above a participant to pop! 🎈");
          }}
          className="rounded-full w-10 h-10"
          title={`Pop Balloon (${popsRemaining} remaining)`}
        >
          🎈
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            // Reactions: heart tap, rose throw (placeholder for now)
            toast.info("Reactions coming soon! 💖🌹");
          }}
          className="rounded-full w-10 h-10"
          title="Reactions"
        >
          ❤️
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowSpeakQueue(!showSpeakQueue)}
          className={cn(
            "rounded-full w-10 h-10",
            isHost && "bg-primary/10 border-primary"
          )}
          title={isHost ? "You are the Host" : "Request to Speak"}
        >
          {isHost ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowMemoryGallery(true)}
          className="rounded-full w-10 h-10"
          title="Love Memory Gallery"
        >
          <Heart className="w-4 h-4" />
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={toggleAudio}
          disabled={moderationState.mutedByAdmin}
          className={cn(
            "rounded-full w-10 h-10",
            !isAudioEnabled && "bg-red-500/10 border-red-500/50 text-red-500",
            moderationState.mutedByAdmin && "opacity-50 cursor-not-allowed"
          )}
          title={moderationState.mutedByAdmin ? "Muted by admin" : isAudioEnabled ? "Mute" : "Unmute"}
        >
          {isAudioEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={toggleVideo}
          disabled={moderationState.cameraDisabledByAdmin}
          className={cn(
            "rounded-full w-10 h-10",
            isVideoEnabled ? "bg-green-500/10 border-green-500/50 text-green-500" : "bg-red-500/10 border-red-500/50 text-red-500",
            moderationState.cameraDisabledByAdmin && "opacity-50 cursor-not-allowed"
          )}
          title={moderationState.cameraDisabledByAdmin ? "Camera disabled by admin" : isVideoEnabled ? "Turn off camera" : "Turn on camera"}
        >
          {isVideoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
        </Button>

        <Button
          variant="destructive"
          size="icon"
          onClick={handleLeave}
          className="rounded-full w-10 h-10"
          title="Leave"
        >
          <PhoneOff className="w-4 h-4" />
        </Button>
      </div>

      {/* Modals */}
      <BoothMatchModal
        isOpen={!!showMatchModal}
        onClose={() => setShowMatchModal(null)}
        matchedUserName={showMatchModal?.userName || ""}
        boothName={booth.name}
        cityName={cityName}
        onOpenPortal={() => setShowMatchModal(null)}
        onContinueInBooth={() => setShowMatchModal(null)}
        onSaveForLater={() => setShowMatchModal(null)}
      />

      <KickedFromBoothModal
        isOpen={!!kickedReason}
        boothName={booth.name}
        reason={kickedReason || ""}
        onClose={() => {
          setKickedReason(null);
          handleLeave();
        }}
        onGoHome={() => {
          setKickedReason(null);
          handleLeave();
        }}
      />

      <LoveMemoryGallery
        isOpen={showMemoryGallery}
        onClose={() => setShowMemoryGallery(false)}
        boothId={booth.id}
        boothName={booth.name}
        eventEndsAt={eventEndsAt}
      />

      <EchoLoveNoteModal
        isOpen={showEchoLoveNote}
        onClose={() => setShowEchoLoveNote(false)}
        boothId={booth.id}
        boothName={booth.name}
        cityName={cityName}
        countryName={countryName}
        eventId={eventId || (booth as any).event_id || undefined}
        onSave={() => {
          setShowEchoLoveNote(false);
          toast.success("Love note saved! 💖");
        }}
      />

      {showSpeakQueue && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground">Request to Speak</h2>
              <button onClick={() => setShowSpeakQueue(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            {isHost ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">You are the host. Manage who can speak.</p>
                <div className="space-y-2">
                  {participants.filter(p => p.user_id !== currentUserId).map((p) => (
                    <div key={p.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {p.profile?.name?.[0] || "U"}
                        </div>
                        <span className="text-sm font-medium">{p.profile?.name || "User"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {currentSpeakerId === p.user_id ? (
                          <Button size="sm" variant="outline" onClick={() => setCurrentSpeakerId(null)}>
                            <MicOff className="w-4 h-4 mr-1" />
                            Revoke
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => setCurrentSpeakerId(p.user_id)}>
                            <Mic className="w-4 h-4 mr-1" />
                            Grant
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {currentSpeakerId === currentUserId
                    ? "You are currently speaking. The host can revoke your mic access."
                    : "Request permission to speak. The host will grant or deny your request."}
                </p>
                {currentSpeakerId !== currentUserId && (
                  <Button onClick={() => {
                    // TODO: Send request via realtime
                    toast.info("Request sent to host");
                  }} className="w-full">
                    <Mic className="w-4 h-4 mr-2" />
                    Request to Speak
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoothArenaView;

